import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import toast from "@markaaz/ui/MzToast";
import { useNavigate } from "react-router";
import * as api from "../api/endpoints/partnerService";
import getLookupTree from "../utils/getLookupTree";
import downloadByUrl from "utils/downloadByUrl";
import { formatJSONData } from "../utils/formatJson";
import LookupMain from "components/BusinessLookup/LookupMain";
import matchFoundTooltipData from "../data/MarkaazMatchFoundTooltip-data.json";
import prettyNameMappingData from "../data/prettyNameMapping-data.json";
import countryCodeData from "../data/CountryCode-data.json";
import goldenRecordKeys from "../data/GoldenRecordKeys-data.json";
import MainLayout from 'layouts/mainLayout'
import { store } from 'reduxStore'

export default function BusinessLookupPage() {
  const [businessMatchError, setBusinessMatchError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupData, setLookupData] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [isRetryPressed, setIsRetryPressed] = useState(false);
  const [bizMatchData, setBizmatchData] = useState();
  const [accessDeniedAlert, setAccessDeniedAlert] = useState(false);
  const [matchFoundTooltip, setMatchFoundTooltip] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedMarkaazId, setSelectedMarkaazId] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [matchConfidenceScore, setMatchConfidenceScore] = useState(null);
  const navigate = useNavigate();

  const matchFoundRef = useRef(null);
  const matchErrorRef = useRef(null);
  const topSectionRef = useRef(null);
  const hasScrolledRef = useRef(false);

  const user = store.getState().userReducer.user

  const countryCode = countryCodeData;
  const mappingDataResult = prettyNameMappingData;
  const mappedGoldenRecordKeys = mapGoldenRecordKeys(goldenRecordKeys)

  useLayoutEffect(() => {
    if (lookupData && matchFoundRef.current && !hasScrolledRef.current) {
      matchFoundRef.current.scrollIntoView({ behavior: "smooth" });
      hasScrolledRef.current = true;
    }
  }, [lookupData]);

  useLayoutEffect(() => {
    if (businessMatchError && matchErrorRef.current) {
      matchErrorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [businessMatchError]);

  useLayoutEffect(() => {
    if (isRetryPressed) {
      topSectionRef.current.scrollIntoView({ behavior: "smooth" });
      setIsRetryPressed(false);
    }
  }, [isRetryPressed]);

  useEffect(() => {
    loadMatchThreshold()
  }, [])

  function handleResetClick() {
    setLookupData(null);
    setBusinessMatchError(false);
  }

  function reset() {
    setBusinessMatchError(false);
    setLookupData(null);
  }

  function handleCopyClick(name, jsonData) {
    const formattedData = formatJSONData(jsonData);
    navigator.clipboard.writeText(formattedData);
  }

  function mapGoldenRecordKeys(goldenRecordKeys) {
    return Object.fromEntries(
      Object.entries(goldenRecordKeys).map(([category, keys]) => [
        category,
        keys.map(key => prettyNameMappingData[category]?.[key] || key),
      ]),
    )
  }

  function loadMatchThreshold() {
    api.getMatchConfidenceTheshold().then(res => {
      setMatchConfidenceScore(res.matchConfidenceThreshold)
    })
  }
  
  async function handleBusinessLookupSubmit(values) {
    setLoading(true);
    reset();
    const config = {
      matchCount: values.matchCount,
      showNonMatches: values.showNonMatches,
      showGrades: values.showGrades,
      bizInfo: values
    };

    api
      .postBusinessMatch(config)
      .then(async (res) => {
        if (!res?.markaaz?.matchResults) {
          setBusinessMatchError(true);
          setBizmatchData(res);
        } else {
          await setLookupInfo(res?.markaaz);
        }
        setLoading(false);
      })
      .catch((error) => {
        setBizmatchData(error?.response?.data);
        if (error.response.status === 403) {
          setAccessDeniedAlert(true);
        }

        setBusinessMatchError(true);
        setLoading(false);
      });
  }

  async function handleDownload(format) {
    setDownloadInProgress(true);
    const jsonData = [];
    let lookupDataCopy = JSON.parse(JSON.stringify(lookupData));

    lookupDataCopy.forEach((item) => {
      const markaazId = item.overview.data["Markaaz ID:"];
      const transformedData = Object.keys(item)
        .map((key) => {
          if (key === "retryData") return null;

          //TODO: Refactoring Needed
          if (format === "pdf") {
            if (key === "hierarchy" && item[key].data != null) {
              for (let i = 0; i < item[key].data["Company Hierarchy"].length; i++) {
                if ("Addresses:" in item[key].data["Company Hierarchy"][i]) {
                  item[key].data["Company Hierarchy"][i]["Addresses:"] = item[key].data["Company Hierarchy"][i][
                    "Addresses:"
                  ].map((address) => {
                    delete address["Address Type:"];
                    return address;
                  });
                }
              }
            } else if (item[key].data != null && "Addresses:" in item[key].data) {
              item[key].data["Addresses:"] = item[key].data["Addresses:"].map((address) => {
                delete address["Address Type:"];
                return address;
              });
            }
          }

          return {
            bizLookupType: key,
            json: item[key].data
          };
        })
        .filter(Boolean);

      jsonData.push({
        entityId: markaazId,
        data: transformedData
      });
    });

    const data = {
      entities: jsonData,
      format
    };


    try {
      const response = await api.downloadReport(data);
      downloadByUrl(response.details.reportUrl);
      setDownloadConfirmOpen(true);
    } catch (e) {
      console.error(e);
    }

    setDownloadOpen(false);
    setDownloadInProgress(false);
  }

  function handleDownloadClose() {
    setDownloadOpen(false);
  }

  function handleDownloadOpen() {
    setDownloadOpen(true);
  }

  function handleDownloadConfirmClose() {
    setDownloadConfirmOpen(false);
  }

  function handleEditThreshold() {
    navigate("/system-level-settings/match-confidence");
  }


  async function setLookupInfo(advanceMatchingResult) {
    if (!advanceMatchingResult) return;

    const results = [];

    for (const business of advanceMatchingResult.matchResults) {

      try {
        let partnerDataResult = {
          bizFirmographic: business.firmographics,
          officersNameMatching: business.name_matching,
          bizCompliance: business.compliance,
          bizHealth: business.business_health,
          bizDiversity: business.diversity,
          bizHierarchy: business.company_hierarchy,
          goldenRecordChanges: business.goldenRecordChanges
        }

        partnerDataResult.bizFirmographic.bizInfo = {
          ...partnerDataResult?.bizInfo
        };

        function getMappedTooltipData(tooltipData, mappingData) {
          let mappedTooltip = {};
          for (const title in tooltipData) {
            for (const label in tooltipData[title]) {
              label in mappingData[title]
                ? (mappedTooltip[mappingData[title][label]] = tooltipData[title][label])
                : (mappedTooltip[label] = tooltipData[title][label]);
            }
          }
          return mappedTooltip;
        }

        function getObjFormPartnerDataResult(obj, ...properties) {
          for (const prop of properties) {
            const { markaazId, traceId, bizInfo } = obj?.[prop] || {};
            if (markaazId !== undefined) {
              return { markaazId, traceId, bizInfo };
            }
          }
          return {};
        }

        const PartnerDataResultObject = getObjFormPartnerDataResult(
          partnerDataResult,
          "bizFirmographic",
          "officersNameMatching",
          "bizCompliance",
          "bizHealth",
          "bizDiversity",
          "bizHierarchy"
        );

        const markaazMatchResult = business;
        const result = getLookupTree(
          mappingDataResult,
          partnerDataResult,
          PartnerDataResultObject,
          markaazMatchResult,
          advanceMatchingResult
        );

        const tooltip = getMappedTooltipData(matchFoundTooltipData, mappingDataResult);
        setMatchFoundTooltip(tooltip);

        let retryData = {};
        for (const key in partnerDataResult) {
          retryData[key] = partnerDataResult[key].showRetry;
        }

        result.retryData = retryData;

        // const updateErrorStatus = PartnerDataResultObject.markaazId || markaaz?.markaazId;
        // if (!updateErrorStatus) {
        //   setBusinessMatchError(true);
        //   return;
        // }

        // setMarkaazId(PartnerDataResultObject.markaazId || markaaz?.markaazId);

        results.push(result);
      } catch (error) {
        setBusinessMatchError(false);
      }
    }

    setLookupData(results);

  }

  function handleRetry() {
    reset();
    setIsRetryPressed(true);
  }

  function handleAlertClose() {
    setBusinessMatchError(false);
  }

  function handleEditClick(markaazId, field, key) {
    setSelectedMarkaazId(markaazId)
    setSelectedField(field)
    setEditingKey(key);
  };

  function getCountryByPriority(addresses) {
    const priorityOrder = ["Primary", "Secondary", "Registered"];

    addresses.sort((a, b) => 
        priorityOrder.indexOf(a["Address Type:"]) - priorityOrder.indexOf(b["Address Type:"])
    );

    for (const address of addresses) {
        if (address["Country Code:"]) {
            return address["Country Code:"];
        }
    }

    return null;
  }


  function getFieldByPrettyName(mapping, targetValue) {
    for (const key in mapping) {
        if (mapping[key] === targetValue) {
            return key;
        } else if (typeof mapping[key] === "object") {
            const result = getFieldByPrettyName(mapping[key], targetValue);
            if (result) {
                return result;
            }
        }
    }
    return null;
  }

  function getUpdatedLookupData(lookupData, markaazId, field, value, user) {
    const updatedData = JSON.parse(JSON.stringify(lookupData));
    const contactFields = ["Corporate Website:","Phone:"];
    
    updatedData.forEach(obj => {
        if (obj.overview?.data?.["Markaaz ID:"] === markaazId) {
            if(contactFields.includes(field)) {
              obj.firmographic.data["Contact:"][field] = value;
            } else {
              obj.firmographic.data[field] = value;
            }
            obj.goldenRecordChanges.bizFirmographic[field].updatedValue = value;
            Object.keys(obj.goldenRecordChanges.bizFirmographic).forEach(key => {
                obj.goldenRecordChanges.bizFirmographic[key].updatedBy = 
                    `${user.firstName || ''} ${user.lastName || ''}`;
            });
        }
    });


    return updatedData
  }

  function handleSaveClick(value) {
    setIsSaving(true)
    const companyDetail = lookupData.find(obj => obj.overview?.data?.["Markaaz ID:"] === selectedMarkaazId)?.overview?.data;
    const country = getCountryByPriority(companyDetail["Addresses:"])
    const field = getFieldByPrettyName(prettyNameMappingData, selectedField)
    
    const config = {
      markaazId: companyDetail["Markaaz ID:"],
      country: country,
      businessName: companyDetail["Company Legal Name:"],
      [field]: value
    };

    api.postBespokeGoldenRecord(config)
     .then((res) => {
        setLookupData(getUpdatedLookupData(lookupData, selectedMarkaazId, selectedField, value, user))
        setEditingKey(null);
        setSelectedMarkaazId(null);
        toast.show("Successully Updated", {placement: "bottom-right"});
      })
      .catch((error) => {
        let errorMessage = 'Something went wrong. Please try after some time.'

        let errors = error.response?.data?.errors

        if (errors) {
          const fieldIdentifier = Object.keys(errors)[0]
          let fieldName = prettyNameMappingData.bizFirmographic[fieldIdentifier]

          // Remove last colon
          if (fieldName.endsWith(':')) {
            fieldName = fieldName.slice(0, -1)
          }

          const message = errors[fieldIdentifier][0]

          errorMessage = `${fieldName} ${message}`
        }

        toast.show(errorMessage, { placement: 'bottom-right', severity: 'error' })
      }).finally(()=>{
        setIsSaving(false)
      });
  };


  function handleCancelClick() {
    setSelectedMarkaazId(null);
    setSelectedField(null);
    setEditingKey(null);
    setIsSaving(false)
  };

  const commonProps = {
    loading: loading,
    bizMatchData: bizMatchData,
    accessDeniedAlert: accessDeniedAlert,
    lookupData: lookupData,
    businessMatchError: businessMatchError,
    matchFoundRef: matchFoundRef,
    matchErrorRef: matchErrorRef,
    markaazConfidenceScore: matchConfidenceScore,
    matchFoundTooltipData: matchFoundTooltip,
    onAlertClose: handleAlertClose,
    onRetry: handleRetry,
    onEditThreshold: handleEditThreshold
  };

  const businessLookupProps = {
    countryCode: countryCode,
    onBusinessLookupSubmit: handleBusinessLookupSubmit,
    onResetClick: handleResetClick
  };

  const downloadProps = {
    downloadOpen: downloadOpen,
    downloadConfirmOpen: downloadConfirmOpen,
    downloadInProgress: downloadInProgress,
    onDownloadOpen: handleDownloadOpen,
    onDownloadClose: handleDownloadClose,
    onDownloadConfirmClose: handleDownloadConfirmClose,
    onCopyClick: handleCopyClick,
    onDownload: handleDownload
  };

  const editGoldenRecordProps = {
    goldenRecordKeys: mappedGoldenRecordKeys,
    editingKey: editingKey,
    isSaving: isSaving,
    onEditClick: handleEditClick,
    onSaveClick: handleSaveClick,
    onCancelClick: handleCancelClick
  }

  return     <MainLayout>
    <LookupMain {...commonProps} {...businessLookupProps} {...downloadProps} {...editGoldenRecordProps}/>
    </MainLayout>
}
